import {
  type BrandVariants,
  createDarkTheme,
  createLightTheme,
  type Theme,
} from "@fluentui/react-components"

// Rampa de marca white-label — docs/05 §4: los 16 tonos BrandVariants (10..160)
// se generan en runtime interpolando en OKLCH alrededor del color primario configurado.

// Color primario por defecto de la instancia (azul Fluent de referencia).
export const DEFAULT_BRAND_COLOR = "#0F6CBD"

interface Rgb {
  r: number
  g: number
  b: number
}

interface Oklch {
  l: number
  c: number
  h: number
}

const HEX_REGEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

export const parseHex = (hex: string): Rgb | null => {
  const match = HEX_REGEX.exec(hex.trim())
  if (!match?.[1]) return null
  let raw = match[1]
  if (raw.length === 3) raw = raw.replace(/./g, (ch) => ch + ch)
  return {
    r: Number.parseInt(raw.slice(0, 2), 16) / 255,
    g: Number.parseInt(raw.slice(2, 4), 16) / 255,
    b: Number.parseInt(raw.slice(4, 6), 16) / 255,
  }
}

const toHexByte = (value: number): string =>
  Math.round(clamp(value, 0, 1) * 255)
    .toString(16)
    .padStart(2, "0")

const formatHex = ({ r, g, b }: Rgb): string => `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}`

const srgbToLinear = (c: number): number =>
  c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4

const linearToSrgb = (c: number): number =>
  c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055

// Conversión sRGB → OKLab → OKLCH (matrices de Björn Ottosson, sin dependencias).
const rgbToOklch = (rgb: Rgb): Oklch => {
  const r = srgbToLinear(rgb.r)
  const g = srgbToLinear(rgb.g)
  const b = srgbToLinear(rgb.b)
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  return { l: okL, c: Math.sqrt(okA * okA + okB * okB), h: Math.atan2(okB, okA) }
}

const oklchToRgb = ({ l, c, h }: Oklch): Rgb => {
  const okA = c * Math.cos(h)
  const okB = c * Math.sin(h)
  const l_ = (l + 0.3963377774 * okA + 0.2158037573 * okB) ** 3
  const m_ = (l - 0.1055613458 * okA - 0.0638541728 * okB) ** 3
  const s_ = (l - 0.0894841775 * okA - 1.291485548 * okB) ** 3
  return {
    r: linearToSrgb(4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_),
    g: linearToSrgb(-1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_),
    b: linearToSrgb(-0.0041960863 * l_ - 0.7034186147 * m_ + 1.7076147009 * s_),
  }
}

const inGamut = ({ r, g, b }: Rgb): boolean =>
  r >= -0.0001 && r <= 1.0001 && g >= -0.0001 && g <= 1.0001 && b >= -0.0001 && b <= 1.0001

// Mapeo de gamut: si el tono cae fuera de sRGB se reduce el croma (búsqueda binaria)
// conservando luminosidad y matiz; el resultado siempre es un hex válido.
const oklchToHex = (color: Oklch): string => {
  let rgb = oklchToRgb(color)
  if (!inGamut(rgb)) {
    let low = 0
    let high = color.c
    for (let i = 0; i < 18; i++) {
      const mid = (low + high) / 2
      rgb = oklchToRgb({ ...color, c: mid })
      if (inGamut(rgb)) low = mid
      else high = mid
    }
    rgb = oklchToRgb({ ...color, c: low })
  }
  return formatHex(rgb)
}

const BRAND_KEYS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160] as const

// Extremos perceptuales de la rampa: 10 casi negro con tinte de marca, 160 casi blanco.
const DARKEST_L = 0.22
const LIGHTEST_L = 0.97

export const brandRampFromHex = (hex: string): BrandVariants => {
  const rgb = parseHex(hex) ?? parseHex(DEFAULT_BRAND_COLOR)
  if (!rgb) throw new Error("Color primario inválido")
  const base = rgbToOklch(rgb)
  // El color configurado se ancla al tono 80 (primario del tema claro en Fluent).
  const anchorL = clamp(base.l, DARKEST_L + 0.08, LIGHTEST_L - 0.12)
  const ramp = {} as Record<(typeof BRAND_KEYS)[number], string>
  for (const key of BRAND_KEYS) {
    if (key === 80 && anchorL === base.l) {
      ramp[key] = formatHex(rgb)
      continue
    }
    let l: number
    let chromaFactor: number
    if (key <= 80) {
      const t = (key - 10) / 70
      l = DARKEST_L + (anchorL - DARKEST_L) * t
      // Hacia el extremo oscuro el croma baja para no saturar de más
      chromaFactor = 0.62 + 0.38 * t
    } else {
      const t = (key - 80) / 80
      l = anchorL + (LIGHTEST_L - anchorL) * t ** 0.9
      // Los tonos claros pierden croma progresivamente (tintes pastel)
      chromaFactor = 1 - 0.78 * t ** 1.1
    }
    ramp[key] = oklchToHex({ l, c: base.c * chromaFactor, h: base.h })
  }
  return ramp
}

export interface BrandThemes {
  light: Theme
  dark: Theme
}

export const themesFromBrandColor = (hex: string): BrandThemes => {
  const ramp = brandRampFromHex(hex)
  return { light: createLightTheme(ramp), dark: createDarkTheme(ramp) }
}

// Luminancia relativa WCAG de un hex (0 = negro, 1 = blanco); útil para validar
// contraste del primario y para la auto-adaptación del logo.
export const relativeLuminance = (hex: string): number | null => {
  const rgb = parseHex(hex)
  if (!rgb) return null
  return 0.2126 * srgbToLinear(rgb.r) + 0.7152 * srgbToLinear(rgb.g) + 0.0722 * srgbToLinear(rgb.b)
}
